import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faCheck,
  faTimes,
  faExternalLinkAlt,
  faFileAlt,
} from '@fortawesome/free-solid-svg-icons';
import { getPageCreationProgress } from '../utils/apis';
import './ChatPageProgress.css';

/**
 * ChatPageProgress
 *
 * Polls the progress endpoint every 2 seconds until done or failed.
 * Uses a ref-based interval to avoid stale closure issues.
 *
 * Props:
 *  - trackingId  {string}
 *  - pageTitle   {string}
 *  - onComplete  {Function} ({ pageId, pageSlug })
 *  - onNavigate  {Function} (pageId)
 */
function ChatPageProgress({ trackingId, pageTitle, pageId: initialPageId, onComplete, onNavigate }) {
  const [progress, setProgress] = useState(0);
  const [message,  setMessage]  = useState('Generating AI content...');
  const [status,   setStatus]   = useState('running');
  const [pageId,   setPageId]   = useState(initialPageId || null);

  // Use refs so the interval callback always has fresh values
  const statusRef     = useRef('running');
  const onCompleteRef = useRef(onComplete);
  const intervalRef   = useRef(null);

  // Keep onCompleteRef current without re-creating the interval
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (!trackingId) return;

    const doPoll = async () => {
      try {
        const data = await getPageCreationProgress(trackingId);
        const newProgress = data.progress || 0;
        const newMessage  = data.message  || '';
        const newStatus   = data.status   || 'running';

        setProgress(newProgress);
        setMessage(newMessage);
        setStatus(newStatus);
        statusRef.current = newStatus;

        if (newStatus === 'completed') {
          setPageId(data.page_id || null);
          if (onCompleteRef.current) {
            onCompleteRef.current({ pageId: data.page_id, pageSlug: data.page_slug });
          }
          // Stop polling
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }

        if (newStatus === 'failed') {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      } catch (err) {
        console.error('[ChatPageProgress] poll error:', err);
      }
    };

    // Poll immediately
    doPoll();

    // Then every 2 seconds
    intervalRef.current = setInterval(doPoll, 2000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  // Only run when trackingId changes — intentionally NOT including status/onComplete
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingId]);

  const steps = [
    { label: 'Design structure', threshold: 5  },
    { label: 'Create page',      threshold: 10 },
    { label: 'Build sections',   threshold: 50 },
    { label: 'Generate content', threshold: 90 },
    { label: 'Finalise',         threshold: 100 },
  ];

  return (
    <div className={`cpp-container cpp-container--${status}`}>

      {/* Header */}
      <div className="cpp-header">
        <FontAwesomeIcon
          icon={
            status === 'completed' ? faCheck
            : status === 'failed'  ? faTimes
            : faFileAlt
          }
          className="cpp-header-icon"
        />
        <div className="cpp-header-text">
          <div className="cpp-title">
            {status === 'completed' && '✓ Page Created!'}
            {status === 'failed'    && 'Page Creation Failed'}
            {status === 'running'   && `Creating "${pageTitle}"...`}
          </div>
          <div className="cpp-subtitle">
            {status === 'running'   && 'AI is generating content for each section'}
            {status === 'completed' && `"${pageTitle}" is ready — click below to open it`}
            {status === 'failed'    && 'Something went wrong. Please try again.'}
          </div>
        </div>
        {status === 'running' && (
          <FontAwesomeIcon icon={faSpinner} spin className="cpp-spinner" />
        )}
      </div>

      {/* Progress bar */}
      <div className="cpp-progress-track">
        <div
          className={`cpp-progress-fill cpp-progress-fill--${status}`}
          style={{ width: `${progress}%`, transition: 'width 0.5s ease' }}
        />
      </div>
      <div className="cpp-progress-label">
        {status === 'running'   && `${progress}% — ${message}`}
        {status === 'completed' && '100% complete'}
        {status === 'failed'    && message}
      </div>

      {/* Step indicators — only while running */}
      {status === 'running' && (
        <div className="cpp-steps">
          {steps.map((step) => (
            <div
              key={step.label}
              className={`cpp-step ${progress >= step.threshold ? 'cpp-step--done' : ''}`}
            >
              <div className="cpp-step-dot">
                {progress >= step.threshold && <FontAwesomeIcon icon={faCheck} />}
              </div>
              <span>{step.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Open button — shown as soon as we have a pageId (even while enriching) */}
      {pageId && (
        <div className="cpp-actions">
          <button
            className={`cpp-btn-open ${status === 'running' ? 'cpp-btn-open--secondary' : ''}`}
            onClick={() => onNavigate && onNavigate(pageId)}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
            {status === 'completed' ? ' Open Page in Builder' : ' Open Page (content still generating...)'}
          </button>
        </div>
      )}
    </div>
  );
}

export default ChatPageProgress;
